import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LazyMotion, domAnimation, m as motion, useInView as useMotionInView } from "framer-motion";
import { useLearningAuth } from "@/context/LearningAuthContext";
import {
  ArrowRight, Phone, Mail, Shield, Heart, Star, Lock, MapPin,
  UserCheck, Users, Target, Clock, Handshake, Eye, TrendingUp,
  Award, Building2, Stethoscope, ChevronRight, Menu, X,
  ClipboardList, Briefcase, MessageSquare
} from "lucide-react";
import {
  HandHeart, UserGear, Crown, RocketLaunch, Path, BookOpen,
  ShieldCheck as PhShieldCheck, ChartLineUp as PhChartLineUp
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { LogoDivider } from "@/components/home/AnimatedWaveDivider";

const LOGO = "/images/brightridge-logo-128.webp";
const PHONE_NUM = "020 3807 0592";
const PHONE_HREF = "tel:+442038070592";

/* ─── ANIMATION ─── */
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const Orb = ({ className, delay = 0, duration = 22 }) => (
  <div className={`absolute rounded-full pointer-events-none hidden md:block ${className}`}
    style={{ animation: `orbFloat ${duration}s ease-in-out infinite`, animationDelay: `${delay}s` }} />
);

const FloatingShape = ({ className, delay = 0 }) => (
  <div className={`absolute pointer-events-none hidden md:block ${className}`}
    style={{ animation: `floatSlow 15s ease-in-out infinite`, animationDelay: `${delay}s` }} />
);

/* ─── DATA ─── */
const heroCards = [
  { icon: Shield, label: "Ofsted-Aware", desc: "Compliance-led process" },
  { icon: UserCheck, label: "Leadership Roles", desc: "RM, Deputy, RI, Ops" },
  { icon: MapPin, label: "UK-Wide", desc: "Nationwide reach" },
  { icon: Award, label: "Free Assessments", desc: "Safeguarding & Ofsted" },
];

const forHomesPoints = [
  { icon: Target, title: "Focused Recruitment", desc: "We specialise in regulated care leadership — Registered Managers, Deputies, RIs and Ops Managers. No generalist shortcuts." },
  { icon: Clock, title: "Fast Response", desc: "We understand the urgency of staffing gaps in children's homes. Our turnaround is built for time-sensitive vacancies." },
  { icon: Shield, title: "Ofsted-Aware Process", desc: "Our screening process considers safeguarding, compliance, and the operational realities of running a registered home." },
  { icon: Handshake, title: "Long-Term Partnerships", desc: "We build ongoing relationships with providers, not one-off transactions. When you need us again, we already know your home." },
];

const forCandidatesPoints = [
  { icon: Lock, text: "Access to confidential, off-market leadership roles" },
  { icon: Eye, text: "Honest insight into homes before you make a move" },
  { icon: TrendingUp, text: "Support stepping up from Deputy to Registered Manager" },
  { icon: Shield, text: "Free safeguarding refresher — prepare before your annual training" },
  { icon: Award, text: "Ofsted Readiness and Career Readiness self-assessments" },
  { icon: Heart, text: "12 refresher modules and career development tools — all free" },
];

const pathwayStages = [
  { id: "support-worker", title: "Support Worker", icon: HandHeart, color: "from-blue-500 to-blue-600" },
  { id: "senior", title: "Senior / Team Leader", icon: Star, color: "from-purple-500 to-purple-600" },
  { id: "deputy", title: "Deputy Manager", icon: UserGear, color: "from-[#E8803A] to-[#D66D2A]" },
  { id: "rm", title: "Registered Manager", icon: Crown, color: "from-emerald-500 to-emerald-600" },
];

const sectors = [
  {
    id: "childrens-homes",
    title: "Children's Residential Care",
    desc: "Leadership recruitment and career development for children's homes across the UK. Registered Managers, Deputies, Team Leaders and operational staff.",
    icon: Building2,
    active: true,
    roles: ["Registered Managers", "Deputy Managers", "Responsible Individuals", "Operations Managers", "Team Leaders", "Support Workers"],
    link: "#for-candidates",
  },
  {
    id: "clinical-care",
    title: "Clinical Care Centres",
    desc: "Specialist recruitment for clinical care environments. Operational leaders, clinical managers and support staff across regulated healthcare settings.",
    icon: Stethoscope,
    active: false,
    roles: [],
  },
];

const whyDifferent = [
  { num: "01", title: "Sector-First, Not Generalist", desc: "We only work in regulated care. Our team understands Ofsted, CQC, safeguarding frameworks, and the operational pressure of running a registered home." },
  { num: "02", title: "Professional Development", desc: "We don't just place people — we prepare them. Free safeguarding refreshers, Ofsted readiness checks, career assessments, AI interview coaching, and bite-sized learning modules through Brightridge Pathways." },
  { num: "03", title: "Intelligence-Led", desc: "We use data tools to map the sector — Ofsted ratings, home locations, registration changes. We know the market before you brief us." },
  { num: "04", title: "Built for the Long Run", desc: "We're building relationships across the care sector, not chasing volume. Your next hire matters to us as much as your first." },
];

/* ─── HEADER ─── */
const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useLearningAuth();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const textClass = scrolled ? "text-white" : "text-[#0C0C0E]";
  const mutedClass = scrolled ? "text-white/50 hover:text-white" : "text-[#0C0C0E]/65 hover:text-[#0C0C0E]";

  return (
    <header data-testid="home-header"
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0C0C0E]/95 backdrop-blur-2xl border-b border-white/[0.06] shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}>
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="home-logo-link">
            <img src={LOGO} alt="" width="40" height="40" className="h-10 w-10" />
            <span className={`font-heading text-[1.4rem] font-extrabold tracking-[0.15em] transition-colors ${textClass}`}>BRIGHTRIDGE</span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <a href="#for-homes" data-testid="nav-for-homes" className={`text-sm font-medium transition-colors ${mutedClass}`}>For Homes</a>
            <a href="#for-candidates" data-testid="nav-for-candidates" className={`text-sm font-medium transition-colors ${mutedClass}`}>For Professionals</a>
            <Link to="/pathways" data-testid="nav-pathways" className={`text-sm font-medium transition-colors ${mutedClass}`}>Pathways</Link>
            <Link to="/blog" data-testid="nav-blog" className={`text-sm font-medium transition-colors ${mutedClass}`}>Blog</Link>
            <Link to="/about" data-testid="nav-about" className={`text-sm font-medium transition-colors ${mutedClass}`}>About</Link>
            <a href={PHONE_HREF} data-testid="nav-phone" className={`flex items-center gap-2 text-sm font-medium transition-colors ${mutedClass}`}>
              <Phone className="w-4 h-4 text-[#E8803A]" />{PHONE_NUM}
            </a>
            {isAuthenticated ? (
              <Button data-testid="nav-dashboard-btn" onClick={() => navigate("/pathways/dashboard")}
                className="bg-[#E8803A] hover:bg-[#D66D2A] text-white font-semibold px-5 h-10 rounded-full text-sm">
                My Dashboard
              </Button>
            ) : (
              <a href="#contact" data-testid="nav-contact-btn">
                <Button className="group bg-[#0C0C0E] hover:bg-[#1a1a1a] text-white font-semibold px-6 h-10 rounded-full text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
                  <span className="flex items-center">Get in Touch<ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></span>
                </Button>
              </a>
            )}
          </div>

          <button data-testid="home-mobile-menu-btn" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} className="md:hidden p-2 rounded-lg" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className={`w-6 h-6 ${textClass}`} /> : <Menu className={`w-6 h-6 ${textClass}`} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-6 border-t border-white/10 pt-4 space-y-3 bg-[#0C0C0E]/95 backdrop-blur-xl -mx-6 px-6 rounded-b-2xl">
            <a href="#for-homes" className="block text-white/70 text-base font-medium py-2" onClick={() => setMobileOpen(false)}>For Homes</a>
            <a href="#for-candidates" className="block text-white/70 text-base font-medium py-2" onClick={() => setMobileOpen(false)}>For Professionals</a>
            <Link to="/pathways" className="block text-white/70 text-base font-medium py-2" onClick={() => setMobileOpen(false)}>Pathways</Link>
            <Link to="/blog" className="block text-white/70 text-base font-medium py-2" onClick={() => setMobileOpen(false)}>Blog</Link>
            <Link to="/about" className="block text-white/70 text-base font-medium py-2" onClick={() => setMobileOpen(false)}>About</Link>
            <a href={PHONE_HREF} className="flex items-center gap-3 text-white/70 text-base font-medium py-2"><Phone className="w-5 h-5 text-[#E8803A]" />{PHONE_NUM}</a>
            <a href="#contact" onClick={() => setMobileOpen(false)}>
              <Button className="w-full bg-[#E8803A] hover:bg-[#D66D2A] text-white font-semibold rounded-full h-12">Get in Touch<ArrowRight className="w-4 h-4 ml-2" /></Button>
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

/* ─── PATHWAY MINI PREVIEW ─── */
const PathwayMini = () => {
  const ref = useRef(null);
  const inView = useMotionInView(ref, { once: true, margin: "-80px" });

  return (
    <div ref={ref} className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <Path className="w-4 h-4 text-[#E8803A]" weight="bold" />
        <span className="text-xs font-semibold text-white/55 uppercase tracking-wider">Career Pathway</span>
      </div>
      <div className="flex items-center gap-3 overflow-x-auto pb-2">
        {pathwayStages.map((stage, i) => {
          const Icon = stage.icon;
          return (
            <motion.div key={stage.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.3 + i * 0.12 }}
              className="flex items-center gap-2 flex-shrink-0"
            >
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${stage.color} flex items-center justify-center shadow-lg`}>
                <Icon className="w-4 h-4 text-white" weight="fill" />
              </div>
              <span className="text-xs font-semibold text-white/50 whitespace-nowrap">{stage.title}</span>
              {i < pathwayStages.length - 1 && <ChevronRight className="w-3 h-3 text-white/15 flex-shrink-0" />}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── PLATFORM DEMO (auto-cycling mockup) ─── */
const demoScreens = [
  {
    id: "dashboard",
    label: "Dashboard",
    header: "Welcome back, Sarah",
    subtitle: "Deputy Manager — 68% Ready",
    content: (
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-white/50 text-[9px] uppercase tracking-wider font-semibold">Readiness Score</span>
          <span className="text-[#E8803A] text-[11px] font-bold">68%</span>
        </div>
        <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#E8803A] to-[#F09050] rounded-full" style={{ width: "68%" }} />
        </div>
        <div className="grid grid-cols-3 gap-1.5 mt-2">
          {[{ n: "12", l: "Modules" }, { n: "4", l: "Saved" }, { n: "3", l: "Quizzes" }].map((s, i) => (
            <div key={i} className="bg-white/[0.04] rounded-md p-2 text-center border border-white/[0.06]">
              <div className="text-white font-bold text-xs">{s.n}</div>
              <div className="text-white/25 text-[8px]">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="text-white/55 text-[9px] uppercase tracking-wider font-semibold mt-2">Continue Learning</div>
        {["Ofsted Inspection Preparation", "Financial Management Basics", "Team Leadership Skills"].map((m, i) => (
          <div key={i} className="flex items-center gap-2 bg-white/[0.03] rounded-md px-2.5 py-1.5 border border-white/[0.06]">
            <div className={`w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold text-white ${["bg-emerald-500/80","bg-blue-500/80","bg-purple-500/80"][i]}`}>
              {m[0]}
            </div>
            <span className="text-white/50 text-[11px] flex-1 truncate">{m}</span>
            <div className="w-8 h-1 bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full bg-[#E8803A]/60 rounded-full" style={{ width: [60,25,10][i] + "%" }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "pathway",
    label: "Pathway",
    header: "Career Pathway",
    subtitle: "Your roadmap to Registered Manager",
    content: (
      <div className="pt-2 pb-1">
        {/* Visual horizontal pathway with nodes and arc */}
        <div className="relative px-2">
          {/* Connection line */}
          <div className="absolute top-[22px] left-[28px] right-[28px] h-[3px] bg-white/[0.06] rounded-full" />
          <div className="absolute top-[22px] left-[28px] h-[3px] bg-gradient-to-r from-blue-500 via-purple-500 to-[#E8803A] rounded-full" style={{ width: "62%" }} />

          {/* Nodes */}
          <div className="relative flex justify-between mb-6">
            {[
              { label: "Support Worker", color: "bg-blue-500", icon: "SW", done: true },
              { label: "Senior / TL", color: "bg-purple-500", icon: "S", done: true },
              { label: "Deputy Manager", color: "bg-[#E8803A]", icon: "DM", active: true },
              { label: "Registered Manager", color: "bg-emerald-500", icon: "RM", locked: true },
            ].map((n, i) => (
              <div key={i} className="flex flex-col items-center" style={{ width: "22%" }}>
                <div className={`w-11 h-11 rounded-xl ${n.done ? n.color : n.active ? n.color : "bg-white/[0.06]"} flex items-center justify-center shadow-lg ${n.active ? "ring-2 ring-[#E8803A]/40 ring-offset-2 ring-offset-[#0C0C0E]" : ""} ${n.locked ? "opacity-40" : ""}`}>
                  {n.done ? (
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <span className="text-white text-[9px] font-bold">{n.icon}</span>
                  )}
                </div>
                <span className={`text-[9px] font-medium mt-2 text-center leading-tight ${n.active ? "text-[#E8803A]" : n.locked ? "text-white/20" : "text-white/60"}`}>{n.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Current stage detail */}
        <div className="bg-white/[0.04] rounded-lg p-3 border border-[#E8803A]/20 mt-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[#E8803A] text-[10px] font-bold uppercase tracking-wider">Deputy Manager — In Progress</span>
            <span className="text-white/60 text-[10px]">68%</span>
          </div>
          <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#E8803A] to-[#F09050] rounded-full" style={{ width: "68%" }} />
          </div>
        </div>
        <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <div className="text-emerald-400 text-[10px] font-semibold">3 more modules to unlock RM pathway</div>
        </div>
      </div>
    ),
  },
  {
    id: "resources",
    label: "Resources",
    header: "Free Downloads",
    subtitle: "Professional templates for your role",
    content: (
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { name: "Supervision Record", cat: "Management", clr: "bg-red-500/20 text-red-400" },
          { name: "Incident Report", cat: "Compliance", clr: "bg-blue-500/20 text-blue-400" },
          { name: "Care Plan Framework", cat: "Operations", clr: "bg-emerald-500/20 text-emerald-400" },
          { name: "Risk Assessment", cat: "Safeguarding", clr: "bg-amber-500/20 text-amber-400" },
          { name: "Staff Rota Template", cat: "Management", clr: "bg-purple-500/20 text-purple-400" },
          { name: "Interview Prep Guide", cat: "Career", clr: "bg-pink-500/20 text-pink-400" },
          { name: "Ofsted Checklist", cat: "Compliance", clr: "bg-blue-500/20 text-blue-400" },
          { name: "Budget Tracker", cat: "Finance", clr: "bg-emerald-500/20 text-emerald-400" },
        ].map((r, i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-white/[0.03] border border-white/[0.06] group hover:border-[#E8803A]/20 transition-colors">
            <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${r.clr}`}>
              <span className="text-[7px] font-bold">PDF</span>
            </div>
            <div className="min-w-0">
              <div className="text-white/60 text-[10px] font-medium truncate">{r.name}</div>
              <div className="text-white/20 text-[8px]">{r.cat}</div>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "module",
    label: "Module",
    header: "Ofsted Inspection Prep",
    subtitle: "Module 3 of 8 — Safeguarding",
    content: (
      <div className="space-y-2.5">
        <div className="bg-white/[0.04] rounded-md p-2.5 border border-white/[0.06]">
          <div className="text-white/50 text-[11px] leading-relaxed">
            During an Ofsted inspection, the inspector will assess safeguarding through observation, staff interviews, and documentation review...
          </div>
        </div>
        <div className="text-white/55 text-[9px] uppercase tracking-wider font-semibold">Key Points</div>
        {["Safeguarding policy — reviewed quarterly", "Staff must complete Level 2 training", "Incident logs within 24 hours", "DBS checks current for all staff"].map((p, i) => (
          <div key={i} className="flex items-center gap-2 px-1">
            <div className="w-1 h-1 rounded-full bg-[#E8803A] flex-shrink-0" />
            <span className="text-white/60 text-[10px]">{p}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#E8803A] to-[#F09050] rounded-full" style={{ width: "37%" }} />
          </div>
          <span className="text-white/55 text-[9px]">37%</span>
        </div>
        <div className="flex gap-1.5">
          <div className="flex-1 py-1.5 bg-white/[0.04] rounded-md text-center text-white/55 text-[9px] border border-white/[0.06]">Previous</div>
          <div className="flex-1 py-1.5 bg-[#E8803A] rounded-md text-center text-white text-[9px] font-semibold">Continue</div>
        </div>
      </div>
    ),
  },
];

const PlatformDemo = ({ navigate, isAuthenticated }) => {
  const [activeScreen, setActiveScreen] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    if (isHovering) return;
    const timer = setInterval(() => {
      setActiveScreen((prev) => (prev + 1) % demoScreens.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isHovering]);

  const screen = demoScreens[activeScreen];

  const goNext = useCallback(() => setActiveScreen((p) => (p + 1) % demoScreens.length), []);
  const goPrev = useCallback(() => setActiveScreen((p) => (p - 1 + demoScreens.length) % demoScreens.length), []);

  return (
    <div className="relative flex items-center gap-3" data-testid="platform-demo"
      onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>

      {/* Left arrow — outside the card */}
      <button onClick={goPrev} data-testid="demo-prev" aria-label="Previous demo screen"
        className="flex-shrink-0 w-10 h-10 rounded-full bg-white/[0.08] hover:bg-[#E8803A] border border-white/20 hover:border-[#E8803A] flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg group">
        <svg className="w-4 h-4 text-[#E8803A] group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
      </button>

      {/* Main card */}
      <div className="relative flex-1">
        {/* Glow */}
        <div className="absolute -inset-3 bg-gradient-to-br from-[#E8803A]/20 via-[#E8803A]/5 to-transparent rounded-3xl blur-xl pointer-events-none" />

        <div className="relative bg-[#0C0C0E] rounded-2xl overflow-hidden border border-white/[0.08] shadow-[0_20px_60px_rgba(0,0,0,0.4),0_0_40px_rgba(232,128,58,0.08)]">
          <Orb className="w-[200px] h-[200px] top-[-15%] right-[-15%] bg-[#E8803A]/[0.06] blur-[60px]" />

          {/* Browser chrome */}
          <div className="relative z-10 px-4 pt-3.5">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-white/10" />
                <div className="w-1.5 h-1.5 rounded-full bg-white/10" />
                <div className="w-1.5 h-1.5 rounded-full bg-white/10" />
              </div>
              <div className="flex-1 h-3.5 bg-white/[0.04] rounded flex items-center px-2">
                <span className="text-white/15 text-[7px] font-mono">brightridge.uk/pathways</span>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-0.5 mb-2.5 bg-white/[0.02] rounded-lg p-0.5 border border-white/[0.04]">
              {demoScreens.map((s, i) => (
                <button key={s.id} onClick={() => setActiveScreen(i)}
                  className={`flex-1 px-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-all duration-300 ${
                    i === activeScreen
                      ? "bg-[#E8803A] text-white shadow-lg shadow-[#E8803A]/30"
                      : "text-white/55 hover:text-white/50 hover:bg-white/[0.04]"
                  }`}
                  data-testid={`demo-tab-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="relative z-10 px-4 pb-3 min-h-[250px]">
            <div key={screen.id} className="animate-demoFadeIn">
              <div className="mb-2.5">
                <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{screen.header}</p>
                <p className="text-white/25 text-[9px] mt-0.5">{screen.subtitle}</p>
              </div>
              {screen.content}
            </div>
          </div>

          {/* Small dot indicators inside */}
          <div className="relative z-10 flex items-center justify-center gap-1.5 pb-2">
            {demoScreens.map((_, i) => (
              <div key={i} className={`rounded-full transition-all duration-500 ${
                i === activeScreen ? "w-4 h-1 bg-[#E8803A]" : "w-1 h-1 bg-white/15"
              }`} />
            ))}
          </div>

          {/* CTA */}
          <div className="relative z-10 px-4 pb-4 flex flex-wrap gap-2">
            <button data-testid="home-learning-cta"
              onClick={() => navigate(isAuthenticated ? "/pathways/dashboard" : "/pathways/register")}
              className="group bg-gradient-to-r from-[#E8803A] via-[#F09050] to-[#E8803A] text-white font-semibold px-5 py-2 rounded-full text-xs transition-all duration-500 shadow-[0_0_16px_rgba(232,128,58,0.3)] hover:shadow-[0_0_30px_rgba(232,128,58,0.5)] hover:scale-105 inline-flex items-center gap-1.5">
              <RocketLaunch className="w-3.5 h-3.5" weight="bold" />
              {isAuthenticated ? "Continue Learning" : "Start Free"}
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Right arrow — outside the card */}
      <button onClick={goNext} data-testid="demo-next" aria-label="Next demo screen"
        className="flex-shrink-0 w-10 h-10 rounded-full bg-white/[0.08] hover:bg-[#E8803A] border border-white/20 hover:border-[#E8803A] flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg group">
        <svg className="w-4 h-4 text-[#E8803A] group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>
    </div>
  );
};

/* ─── PAGE ─── */
export default function NewHomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useLearningAuth();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    requestAnimationFrame(() => { document.documentElement.style.scrollBehavior = ''; });
  }, []);

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const heroRef = useRef(null);
  const heroInView = useMotionInView(heroRef, { once: true, margin: "-50px" });

  return (
    <LazyMotion features={domAnimation}>
    <>
      <SiteHeader />
      <Helmet>
        <title>Brightridge | Specialist Recruitment for Regulated Care</title>
        <meta name="description" content="Specialist recruitment for UK regulated care. Children's residential homes, clinical care centres. Leadership roles, career development, and a free Brightridge Pathways learning platform." />
        <meta name="keywords" content="regulated care recruitment, children's homes recruitment, registered manager jobs, care home leadership, Brightridge, UK care jobs, deputy manager jobs, children's home careers, Ofsted compliant recruitment" />
        <link rel="canonical" href="https://www.brightridge.uk" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Brightridge | Specialist Recruitment for Regulated Care" />
        <meta property="og:description" content="Specialist recruitment for UK regulated care. Leadership roles, career development, and free learning tools for children's homes professionals." />
        <meta property="og:url" content="https://www.brightridge.uk" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Brightridge | Specialist Recruitment for Regulated Care" />
        <meta name="twitter:description" content="Specialist recruitment for UK regulated care. Leadership roles, career development, and free learning tools." />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Brightridge",
            "legalName": "Brightridge Partners Ltd",
            "url": "https://www.brightridge.uk",
            "logo": "https://www.brightridge.uk/images/brightridge-logo.png",
            "description": "Specialist recruitment for UK regulated care. Children's residential homes and clinical care centres.",
            "telephone": "+442038070592",
            "email": "hello@brightridge.uk",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "GB"
            },
            "sameAs": ["https://www.linkedin.com/company/brightridge-partners/"],
            "knowsAbout": ["Children's residential care recruitment", "Registered Manager recruitment", "Care home leadership", "Ofsted compliance"],
            "areaServed": {
              "@type": "Country",
              "name": "United Kingdom"
            }
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Brightridge",
            "url": "https://www.brightridge.uk",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.brightridge.uk/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I become a Registered Manager in a children's home?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "To become a Registered Manager in a children's home, you typically need a Level 5 Diploma in Leadership and Management for Residential Childcare, significant experience in children's residential care, and must pass Ofsted's fit person interview. Brightridge offers a free career development platform called Brightridge Pathways to help you prepare."
                }
              },
              {
                "@type": "Question",
                "name": "What is Brightridge Pathways?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Brightridge Pathways is a free career development platform for children's residential care professionals. It includes training modules, downloadable templates, professional guides, and a personal readiness score to help you progress from Support Worker to Registered Manager."
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      <main>
        {/* ════════════════════ HERO (LIGHT) ════════════════════ */}
        <section className="relative min-h-screen flex items-center overflow-hidden bg-[#FAFAF8]">
          <Orb className="w-[500px] h-[500px] -top-[10%] -right-[10%] bg-gradient-to-br from-[#E8803A]/20 via-[#E8803A]/10 to-transparent blur-[80px]" delay={0} duration={25} />
          <Orb className="w-[400px] h-[400px] top-[50%] -left-[10%] bg-gradient-to-tr from-[#E8803A]/15 to-transparent blur-[60px]" delay={5} duration={20} />
          <Orb className="w-[200px] h-[200px] top-[30%] right-[20%] bg-[#F09050]/20 blur-[50px]" delay={2} duration={18} />
          <FloatingShape className="w-3 h-3 rounded-full bg-[#E8803A]/30 top-[20%] left-[15%]" delay={0} />
          <FloatingShape className="w-2 h-2 rounded-full bg-[#E8803A]/25 top-[40%] right-[25%]" delay={3} />
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "linear-gradient(#0C0C0E 1px, transparent 1px), linear-gradient(90deg, #0C0C0E 1px, transparent 1px)", backgroundSize: "60px 60px", transform: `translateY(${scrollY * 0.1}px)` }} />

          <div ref={heroRef} className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 pt-28 pb-20 md:pt-36 md:pb-28 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
              <div>
                <motion.div className="mb-6" initial={false} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1, duration: 0.8 }}>
                  <span data-testid="home-hero-badge" className="inline-flex items-center gap-2 text-[#E8803A] text-sm font-semibold tracking-[0.2em] uppercase">
                    <span className="w-8 h-px bg-[#E8803A] animate-pulse" />Specialist Recruitment
                  </span>
                </motion.div>

                <motion.h1 data-testid="home-hero-title"
                  className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-[3.6rem] font-bold leading-[1.08] tracking-tight text-[#0C0C0E]"
                  initial={false} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.2, duration: 0.8 }}
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  The right leaders for the homes that{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E8803A] via-[#F09050] to-[#D66D2A]">need them most.</span>
                </motion.h1>

                <motion.p className="mt-8 text-lg md:text-xl text-[#0C0C0E]/60 max-w-lg leading-relaxed"
                  initial={false} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.4, duration: 0.8 }}>
                  Specialist recruitment for UK regulated care. Leadership roles, operational support, and a free career development platform for children's residential homes and care professionals.
                </motion.p>

                <motion.div className="mt-10 flex flex-col sm:flex-row items-start gap-5"
                  initial={false} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.6, duration: 0.8 }}>
                  <a href="#for-homes" data-testid="home-hero-homes-cta">
                    <Button className="group relative bg-[#0C0C0E] hover:bg-[#1a1a1a] text-white font-semibold px-8 h-13 rounded-full text-base transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:scale-105">
                      <span className="flex items-center">I'm a Home / Provider<ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" /></span>
                    </Button>
                  </a>
                  <a href="#for-candidates" data-testid="home-hero-candidates-cta">
                    <Button variant="ghost" className="text-[#0C0C0E]/70 hover:text-[#0C0C0E] hover:bg-[#0C0C0E]/5 font-medium px-6 h-13 rounded-full text-base transition-all duration-300">
                      I'm a Care Professional
                    </Button>
                  </a>
                </motion.div>
              </div>

              {/* RIGHT: cards */}
              <motion.div className="relative" initial={false} animate={heroInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.3, duration: 0.8 }}>
                <div className="grid grid-cols-2 gap-4">
                  {heroCards.map((item, i) => (
                    <motion.div key={item.label}
                      initial={false} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.5 + i * 0.1, duration: 0.6 }}
                      className="group relative bg-white rounded-2xl p-6 transition-all duration-500 hover:-translate-y-3 hover:scale-[1.02] shadow-lg hover:shadow-2xl border border-[#0C0C0E]/[0.04] hover:border-[#E8803A]/30 overflow-hidden"
                      data-testid={`home-hero-card-${i}`}>
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#E8803A]/[0.08] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      <div className="relative z-10 w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8803A] to-[#D66D2A] flex items-center justify-center mb-4 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-lg group-hover:shadow-[0_0_30px_rgba(232,128,58,0.5)]">
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="relative z-10 font-heading text-base font-bold text-[#0C0C0E] mb-1">{item.label}</h2>
                      <p className="relative z-10 text-sm text-[#0C0C0E]/65">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
                <motion.p className="text-center mt-6 text-sm text-[#0C0C0E]/60"
                  initial={false} animate={heroInView ? { opacity: 1 } : {}} transition={{ delay: 1 }}>
                  Regulated care recruitment, <span className="text-[#E8803A] font-medium">handled with care.</span>
                </motion.p>
              </motion.div>
            </div>
          </div>

          <div className="absolute bottom-8 left-0 right-0 flex justify-center z-10">
            <div className="flex flex-col items-center gap-2 animate-bounce">
              <span className="text-[#0C0C0E]/60 text-xs tracking-widest uppercase font-medium">Scroll</span>
              <div className="w-px h-6 md:h-8 bg-gradient-to-b from-[#E8803A]/60 to-transparent" />
            </div>
          </div>
        </section>

        {/* ─── DIVIDER: light → dark ─── */}
        <LogoDivider fillColor="#0C0C0E" bgColor="#FAFAF8" />

        {/* ════════════════════ FOR HOMES (DARK) ════════════════════ */}
        <section id="for-homes" className="py-24 md:py-32 bg-[#0C0C0E] relative overflow-hidden">
          <Orb className="w-[600px] h-[600px] top-[10%] right-[-10%] bg-[#E8803A]/[0.06] blur-[120px]" delay={2} />
          <Orb className="w-[400px] h-[400px] bottom-[10%] left-[-8%] bg-[#E8803A]/[0.04] blur-[100px]" delay={5} />

          <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              variants={{ visible: { transition: { staggerChildren: 0.12 } } }}>
              <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#E8803A]" />
                <span className="text-[#E8803A] text-sm font-semibold tracking-[0.2em] uppercase">For Children's Homes</span>
              </motion.div>
              <motion.h2 variants={fadeUp}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight max-w-3xl"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Find the leadership your home needs.{" "}
                <span className="text-white/55">Without the noise.</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg mb-16 max-w-2xl leading-relaxed">
                When key roles sit empty, care quality, staff morale and Ofsted outcomes are all at risk. We help children's homes find the right leaders — quickly and reliably.
              </motion.p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {forHomesPoints.map((item, i) => (
                  <motion.div key={i} variants={fadeUp}
                    className="group relative bg-white/[0.03] backdrop-blur-sm rounded-2xl p-7 md:p-8 border border-white/[0.08] hover:border-[#E8803A]/40 hover:bg-white/[0.06] transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                    data-testid={`for-homes-card-${i}`}>
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#E8803A]/[0.12] via-[#E8803A]/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    <div className="relative z-10 flex items-start gap-5">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8803A] to-[#D66D2A] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#E8803A]/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{item.title}</h3>
                        <p className="text-white/60 leading-relaxed text-sm group-hover:text-white/60 transition-colors">{item.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeUp} className="mt-12 flex flex-col sm:flex-row items-center gap-6">
                <a href="#contact">
                  <Button data-testid="homes-cta-speak" className="group bg-gradient-to-r from-[#E8803A] via-[#F09050] to-[#E8803A] text-white font-semibold px-8 h-12 rounded-full transition-all duration-300 shadow-[0_0_20px_rgba(232,128,58,0.3)] hover:shadow-[0_0_40px_rgba(232,128,58,0.5)] hover:-translate-y-0.5">
                    <span className="flex items-center">Speak With Our Team<ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></span>
                  </Button>
                </a>
                <a href={PHONE_HREF} className="flex items-center gap-2 text-white/60 hover:text-white/70 transition-colors text-sm font-medium">
                  <Phone className="w-4 h-4 text-[#E8803A]" />{PHONE_NUM}
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ─── DIVIDER: dark → light ─── */}
        <LogoDivider fillColor="#FAFAF8" bgColor="#0C0C0E" />

        {/* ════════════════════ FOR CANDIDATES (LIGHT) ════════════════════ */}
        <section id="for-candidates" className="py-24 md:py-32 bg-[#FAFAF8] relative overflow-hidden">
          <Orb className="w-[500px] h-[500px] top-[10%] right-[-10%] bg-[#E8803A]/[0.08] blur-[100px]" delay={0} />
          <Orb className="w-[300px] h-[300px] bottom-[20%] left-[-5%] bg-[#E8803A]/[0.06] blur-[60px]" delay={5} />

          <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#E8803A]" />
                <span className="text-[#E8803A] text-sm font-semibold tracking-[0.2em] uppercase">For Care Professionals</span>
              </motion.div>
              <motion.h2 variants={fadeUp}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0C0C0E] mb-6 tracking-tight max-w-3xl"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Your next role in{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E8803A] via-[#F09050] to-[#D66D2A]">children's residential care.</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="text-[#0C0C0E]/65 text-base md:text-lg mb-12 max-w-2xl leading-relaxed">
                Confidential leadership opportunities across the UK, plus a free learning platform to prepare you for your next step — whether that's Deputy to RM, or your first Team Leader role.
              </motion.p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left: Why points */}
                <motion.div variants={fadeUp}>
                  <div className="grid grid-cols-1 gap-4">
                    {forCandidatesPoints.map((item, i) => (
                      <div key={i} className="group flex items-start gap-4 p-4 rounded-xl bg-white border border-[#E8E8E3] hover:border-[#E8803A]/30 hover:shadow-lg transition-all duration-300"
                        data-testid={`candidate-point-${i}`}>
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E8803A] to-[#D66D2A] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <item.icon className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-[#0C0C0E]/60 text-sm leading-relaxed font-medium pt-2 group-hover:text-[#0C0C0E]/80 transition-colors">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Right: Brightridge Pathways Live Demo */}
                <motion.div variants={fadeUp}>
                  <PlatformDemo navigate={navigate} isAuthenticated={isAuthenticated} />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── DIVIDER: light → light (subtle) ─── */}
        {/* Old dark "Know where you stand" assessments showcase removed — superseded by the
            "Six tools, one outcome" overview section below which covers all 6 free tools cleanly. */}

        {/* (Old dark "Know where you stand" assessment showcase removed — the 3 assessment
            cards are now part of the unified "Six tools, one outcome" overview below.) */}

        {/* (Removed the old dark "Know where you stand" assessments section — now covered
            cleanly by the unified "Six tools, one outcome" overview below.) */}

        {/* ════════════════════ EVERYTHING YOU GET — single-glance overview ════════════════════ */}
        <section id="free-tools" className="py-24 md:py-32 bg-[#FAFAF8] relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              variants={{ visible: { transition: { staggerChildren: 0.08 } } }}>
              <motion.div variants={fadeUp} className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px w-8 bg-[#E8803A]" />
                  <span className="text-[#E8803A] text-sm font-semibold tracking-[0.2em] uppercase">Everything you get · 100% Free</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0C0C0E] mb-4 tracking-tight max-w-3xl"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Six tools, one outcome:
                  <span className="block text-[#0C0C0E]/60 mt-1">walk into your next interview ready.</span>
                </h2>
                <p className="text-[#0C0C0E]/55 text-base md:text-lg max-w-2xl leading-relaxed">
                  One free account unlocks every tool below. Built specifically for UK children's residential care — Support Worker through to Responsible Individual. No paywalls, no upsells.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { tag: "NEW · AI", title: "Care Interview Coach", body: "41 UK-specific questions, AI mock interviews, 5 safeguarding scenarios, employer-tailored briefs and a live readiness score.", who: "All roles · Free account", href: "/pathways/interview-coach", color: "bg-[#E8803A]", Icon: MessageSquare, highlight: true },
                  { tag: "Assess", title: "3 Self-Assessments", body: "Quick scenario-based knowledge checks — Safeguarding Confidence, Ofsted Readiness, Career Readiness. Spot your gaps in 5-10 minutes.", who: "All roles · No login to start", href: "/pathways/assessments", color: "bg-emerald-500", Icon: ClipboardList },
                  { tag: "Learn", title: "12 Refresher Modules", body: "Bite-sized learning across every category. Read, watch, test. Perfect for closing gaps an assessment surfaced — or just keeping current.", who: "All roles · Free account", href: "/pathways/categories", color: "bg-purple-600", Icon: BookOpen },
                  { tag: "Plan", title: "Career Pathway Roadmap", body: "Structured learning from Support Worker through Senior, Deputy, Registered Manager and Responsible Individual. Know what 'good' looks like.", who: "All roles · Free account", href: "/pathways/pathway", color: "bg-blue-600", Icon: Briefcase },
                  { tag: "Use", title: "PDF Templates & Tools", body: "Ready-to-use templates: supervision records, restraint logs, safeguarding logs, audit checklists, post-incident reflections, salary benchmarks.", who: "All roles · Free account", href: "/pathways/resources", color: "bg-amber-500", Icon: Shield },
                  { tag: "Stay sharp", title: "Sector Insights", body: "Articles, guides, and updates from the UK children's residential care sector. Plain-English commentary on Ofsted, regulations, the workforce.", who: "All roles · No login", href: "/blog", color: "bg-slate-700", Icon: Star },
                ].map((tool, i) => (
                  <motion.button key={i} variants={fadeUp}
                    onClick={() => navigate(tool.href)}
                    data-testid={`home-tool-${i}`}
                    className={`group text-left bg-white border ${tool.highlight ? "border-[#E8803A]/40 hover:border-[#E8803A]/70" : "border-[#E8E8E3] hover:border-[#0C0C0E]/30"} rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl relative overflow-hidden`}>
                    {tool.highlight && (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#E8803A]/[0.06] to-transparent pointer-events-none" />
                      )}
                    {tool.highlight && (
                      <div className="absolute top-3 right-3 text-[9px] tracking-[0.15em] uppercase font-bold text-[#E8803A] bg-[#E8803A]/15 px-2 py-0.5 rounded">{tool.tag}</div>
                    )}
                    <div className="relative z-10">
                      <div className={`w-12 h-12 rounded-xl ${tool.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500`}>
                        <tool.Icon className="w-6 h-6 text-white" />
                      </div>
                      {!tool.highlight && (
                        <div className="text-[9px] tracking-[0.15em] uppercase font-bold text-[#0C0C0E]/60 mb-1">{tool.tag}</div>
                      )}
                      <h3 className="text-lg font-bold text-[#0C0C0E] mb-2 leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{tool.title}</h3>
                      <p className="text-sm text-[#0C0C0E]/55 leading-relaxed mb-4 group-hover:text-[#0C0C0E]/70 transition-colors">{tool.body}</p>
                      <div className="flex items-center justify-between pt-3 border-t border-[#E8E8E3]">
                        <span className="text-xs text-[#0C0C0E]/60">{tool.who}</span>
                        <ArrowRight className="w-4 h-4 text-[#E8803A] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white rounded-2xl p-6 border border-[#E8E8E3]">
                <div>
                  <h3 className="text-base font-bold text-[#0C0C0E] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>One free account unlocks all six.</h3>
                  <p className="text-sm text-[#0C0C0E]/55">No credit card. No upsell. 30 seconds to sign up.</p>
                </div>
                <button onClick={() => navigate(isAuthenticated ? "/pathways/dashboard" : "/pathways/register")} data-testid="home-overview-cta"
                  className="bg-[#E8803A] hover:bg-[#D66D2A] text-white rounded-full px-6 py-3 text-sm font-bold transition-colors flex items-center gap-2 whitespace-nowrap">
                  {isAuthenticated ? "Go to my dashboard" : "Create free account"} <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ─── DIVIDER: light → dark ─── */}
        <LogoDivider fillColor="#0C0C0E" bgColor="#FAFAF8" />

        {/* ════════════════════ WHY BRIGHTRIDGE (DARK) ════════════════════ */}
        <section className="py-24 md:py-32 bg-[#0C0C0E] relative overflow-hidden">
          <Orb className="w-[500px] h-[500px] top-[10%] left-[-10%] bg-[#E8803A]/[0.04] blur-[120px]" delay={1} />
          <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.div variants={fadeUp} className="text-center mb-16">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 mb-8">
                  <Star className="w-4 h-4 text-[#E8803A]" />
                  <span className="text-sm font-semibold text-white/60">Why We're Different</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Not a generalist recruiter.{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E8803A] via-[#F09050] to-[#E8803A]">A sector specialist.</span>
                </h2>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {whyDifferent.map((item, i) => (
                  <motion.div key={i} variants={fadeUp}
                    className="group relative bg-white/[0.03] rounded-2xl p-8 border border-white/10 hover:border-[#E8803A]/40 hover:bg-white/[0.05] transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                    data-testid={`why-card-${i}`}>
                    <div className="absolute -top-4 -right-2 font-heading text-[6rem] font-bold text-[#E8803A]/[0.04] leading-none select-none pointer-events-none group-hover:text-[#E8803A]/[0.08] transition-all duration-500">{item.num}</div>
                    <div className="relative z-10">
                      <h3 className="text-lg font-bold text-white mb-3 group-hover:text-[#E8803A] transition-colors" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{item.title}</h3>
                      <p className="text-white/50 leading-relaxed text-sm group-hover:text-white/70 transition-colors">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── DIVIDER: dark → light ─── */}
        <LogoDivider fillColor="#FAFAF8" bgColor="#0C0C0E" />

        {/* ════════════════════ SECTORS (LIGHT) ════════════════════ */}
        <section id="sectors" className="py-24 md:py-32 bg-[#FAFAF8] relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              variants={{ visible: { transition: { staggerChildren: 0.15 } } }}>
              <motion.div variants={fadeUp} className="text-center mb-16">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0C0C0E]/5 border border-[#0C0C0E]/10 mb-8">
                  <Building2 className="w-4 h-4 text-[#E8803A]" />
                  <span className="text-sm font-semibold text-[#0C0C0E]/60">Our Sectors</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0C0C0E] mb-4"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Specialist recruitment,{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E8803A] via-[#F09050] to-[#D66D2A]">sector by sector.</span>
                </h2>
                <p className="text-[#0C0C0E]/65 text-base md:text-lg max-w-2xl mx-auto">We go deep — not wide. Each sector we enter gets a dedicated team, tailored process, and genuine market knowledge.</p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {sectors.map((sector) => (
                  <motion.div key={sector.id} variants={fadeUp}
                    className={`group relative rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2 ${
                      sector.active ? "bg-white border border-[#E8E8E3] shadow-lg hover:shadow-2xl hover:border-[#E8803A]/30" : "bg-[#F5F5F3] border border-[#E8E8E3]/50 opacity-70"
                    }`}
                    data-testid={`sector-${sector.id}`}>
                    <div className="p-8 md:p-10">
                      <div className="flex items-center gap-4 mb-6">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                          sector.active ? "bg-[#0C0C0E] group-hover:bg-[#E8803A] transition-colors duration-400" : "bg-[#0C0C0E]/20"
                        }`}>
                          <sector.icon className={`w-7 h-7 ${sector.active ? "text-[#E8803A] group-hover:text-white transition-colors duration-400" : "text-[#0C0C0E]/60"}`} />
                        </div>
                        <div>
                          <h3 className="font-heading text-xl font-bold text-[#0C0C0E]">{sector.title}</h3>
                          {!sector.active && (
                            <span className="text-xs font-semibold text-[#E8803A] bg-[#E8803A]/10 px-2 py-0.5 rounded-full">Coming Soon</span>
                          )}
                        </div>
                      </div>
                      <p className="text-[#0C0C0E]/65 leading-relaxed text-sm mb-6">{sector.desc}</p>
                      {sector.active && sector.roles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6">
                          {sector.roles.map((role) => (
                            <span key={role} className="px-3 py-1 text-xs text-[#0C0C0E]/65 bg-[#F5F5F3] rounded-full border border-[#E8E8E3] group-hover:border-[#E8803A]/20 transition-colors">{role}</span>
                          ))}
                        </div>
                      )}
                      {sector.active && sector.link && (
                        <Link to={sector.link} className="inline-flex items-center gap-2 text-[#E8803A] text-sm font-semibold hover:gap-3 transition-all" data-testid={`sector-${sector.id}-link`}>
                          Explore<ArrowRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── DIVIDER: light → dark ─── */}
        <LogoDivider fillColor="#0C0C0E" bgColor="#FAFAF8" />

        {/* ════════════════════ CONTACT (DARK) ════════════════════ */}
        <section id="contact" className="py-24 md:py-32 bg-[#0C0C0E] relative overflow-hidden">
          <Orb className="w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#E8803A]/[0.06] blur-[150px]" />

          <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.div variants={fadeUp} className="text-center mb-16">
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">Get in Touch</h2>
                <p className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto">Whether you're a provider looking for leadership or a care professional exploring your next move — we'd love to hear from you.</p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                <motion.div variants={fadeUp}>
                  <div className="bg-gradient-to-br from-[#E8803A] to-[#D66D2A] rounded-3xl p-10 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.08)_50%,transparent_75%)] animate-shimmer" />
                    <div className="relative z-10">
                      <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-8">
                        <Phone className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-white mb-4">Prefer a Quick Chat?</h3>
                      <p className="text-white/80 mb-8">Give us a call — it's confidential, no pressure, and we'll give you an honest steer whether we can help or not.</p>
                      <a href={PHONE_HREF} data-testid="contact-phone-btn"
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#E8803A] font-semibold rounded-full hover:bg-white/90 transition-all text-lg shadow-lg">
                        <Phone className="w-5 h-5" />{PHONE_NUM}
                      </a>
                      <div className="mt-4">
                        <a href="mailto:hello@brightridge.uk" data-testid="contact-email-btn"
                          className="text-white/70 text-sm hover:text-white transition-colors flex items-center justify-center gap-2">
                          <Mail className="w-4 h-4" />hello@brightridge.uk
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-5 bg-white/[0.03] border border-white/[0.06] rounded-2xl">
                    <p className="text-white/60 text-sm text-center">All conversations are completely confidential. We never share your details without your permission.</p>
                  </div>
                </motion.div>

                <motion.div variants={fadeUp}>
                  <ContactForm />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ════════════════════ FOOTER ════════════════════ */}
        <footer className="bg-[#0C0C0E] border-t border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10 mb-12">

              {/* Brand column */}
              <div className="col-span-2 md:col-span-4">
                <div className="flex items-center gap-3 mb-4">
                  <img src={LOGO} alt="" width="36" height="36" loading="lazy" className="h-9 w-9" />
                  <span className="font-heading text-lg font-bold tracking-[0.15em] text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>BRIGHTRIDGE</span>
                </div>
                <p className="text-white/50 text-sm leading-relaxed mb-5 max-w-xs">
                  Specialist recruitment and career development for UK children's residential care. Honest, sector-led, intelligence-driven.
                </p>
                <div className="space-y-2 text-sm">
                  <a href="mailto:hello@brightridge.uk" className="flex items-center gap-2 text-white/60 hover:text-[#E8803A] transition-colors" data-testid="footer-email">
                    <Mail className="w-3.5 h-3.5" /> hello@brightridge.uk
                  </a>
                  <a href="tel:+442038070592" className="flex items-center gap-2 text-white/60 hover:text-[#E8803A] transition-colors" data-testid="footer-phone">
                    <Phone className="w-3.5 h-3.5" /> 020 3807 0592
                  </a>
                  <a href="https://www.linkedin.com/company/brightridge-partners/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/60 hover:text-[#E8803A] transition-colors" data-testid="footer-linkedin">
                    <ArrowRight className="w-3.5 h-3.5" /> LinkedIn
                  </a>
                </div>
              </div>

              {/* For Professionals */}
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8803A] mb-4">For Professionals</h3>
                <ul className="space-y-2.5 text-sm">
                  <li><Link to="/pathways" className="text-white/50 hover:text-white transition-colors" data-testid="footer-pathways">Brightridge Pathways</Link></li>
                  <li><Link to="/pathways/interview-coach" className="text-white/50 hover:text-white transition-colors" data-testid="footer-coach">AI Interview Coach</Link></li>
                  <li><Link to="/pathways/assessments" className="text-white/50 hover:text-white transition-colors" data-testid="footer-assess">Free Self-Assessments</Link></li>
                  <li><Link to="/pathways/register" className="text-white/50 hover:text-white transition-colors" data-testid="footer-register">Create Free Account</Link></li>
                  <li><Link to="/pathways/login" className="text-white/50 hover:text-white transition-colors" data-testid="footer-login">Log In</Link></li>
                </ul>
              </div>

              {/* For Providers */}
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8803A] mb-4">For Providers</h3>
                <ul className="space-y-2.5 text-sm">
                  <li><a href="#how-it-works" className="text-white/50 hover:text-white transition-colors">How We Work</a></li>
                  <li><a href="#sectors" className="text-white/50 hover:text-white transition-colors">Sectors We Cover</a></li>
                  <li><a href="#contact" className="text-white/50 hover:text-white transition-colors">Brief a Role</a></li>
                  <li><a href="#contact" className="text-white/50 hover:text-white transition-colors">Talent Mapping</a></li>
                </ul>
              </div>

              {/* Resources */}
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8803A] mb-4">Resources</h3>
                <ul className="space-y-2.5 text-sm">
                  <li><Link to="/blog" className="text-white/50 hover:text-white transition-colors" data-testid="footer-blog">Sector Insights</Link></li>
                  <li><Link to="/pathways/resources" className="text-white/50 hover:text-white transition-colors" data-testid="footer-resources">PDF Templates</Link></li>
                  <li><Link to="/pathways/categories" className="text-white/50 hover:text-white transition-colors">Refresher Modules</Link></li>
                  <li><Link to="/pathways/pathway" className="text-white/50 hover:text-white transition-colors">Career Pathway</Link></li>
                </ul>
              </div>

              {/* Company */}
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8803A] mb-4">Company</h3>
                <ul className="space-y-2.5 text-sm">
                  <li><Link to="/about" className="text-white/50 hover:text-white transition-colors" data-testid="footer-about">About Us</Link></li>
                  <li><a href="#contact" className="text-white/50 hover:text-white transition-colors">Contact</a></li>
                  <li><Link to="/blog" className="text-white/50 hover:text-white transition-colors">Blog</Link></li>
                  <li><Link to="/privacy-policy" className="text-white/50 hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
                </ul>
              </div>
            </div>

            {/* SEO key-roles row — crawlable internal text */}
            <div className="border-t border-white/[0.06] pt-8 pb-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/55 mb-3">Roles we recruit · UK Children's Residential Care</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-white/55">
                {["Registered Manager", "Deputy Manager", "Responsible Individual", "Operations Manager", "Team Leader", "Senior Support Worker", "Support Worker", "Therapeutic Lead", "Clinical Lead"].map((r) => (
                  <span key={r}>{r}</span>
                ))}
              </div>
            </div>

            {/* Bottom row */}
            <div className="border-t border-white/[0.06] pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/55">
              <p>&copy; {new Date().getFullYear()} Brightridge Partners Ltd. Registered in England &amp; Wales. All rights reserved.</p>
              <div className="flex items-center gap-5">
                <Link to="/privacy-policy" className="hover:text-[#E8803A] transition-colors">Privacy</Link>
                <Link to="/privacy-policy#cookies" className="hover:text-[#E8803A] transition-colors">Cookies</Link>
                <a href="mailto:hello@brightridge.uk" className="hover:text-[#E8803A] transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </footer>

        <style>{`
          @keyframes orbFloat {
            0%, 100% { transform: translateY(0) translateX(0); }
            25% { transform: translateY(-25px) translateX(12px); }
            50% { transform: translateY(0) translateX(25px); }
            75% { transform: translateY(25px) translateX(12px); }
          }
          @keyframes floatSlow {
            0%, 100% { transform: translateY(0) scale(1); }
            50% { transform: translateY(-10px) scale(1.1); }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes demoFadeIn {
            0% { opacity: 0; transform: translateY(8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-shimmer { animation: shimmer 3s infinite; }
          .animate-demoFadeIn { animation: demoFadeIn 0.4s ease-out forwards; }
        `}</style>
      </main>
    </>
    </LazyMotion>
  );
}

/* ─── CONTACT FORM ─── */
function ContactForm() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "candidate", message: "" });
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      setSendError(false);
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email,
          company: form.type === "provider" ? "Provider Enquiry" : "Candidate Enquiry",
          message: `[Homepage Contact — ${form.type}]\nPhone: ${form.phone}\n\n${form.message}`,
        }),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      setForm({ name: "", email: "", phone: "", type: "candidate", message: "" });
    } catch {
      setSendError(true);
    } finally { setSubmitting(false); }
  };

  if (sent) {
    return (
      <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl text-center" data-testid="contact-success">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
          <Heart className="w-8 h-8 text-emerald-500" />
        </div>
        <h3 className="font-heading text-xl font-bold text-[#0C0C0E] mb-3">Thank you!</h3>
        <p className="text-[#0C0C0E]/60 text-sm leading-relaxed">We'll review your details and be in touch shortly. All enquiries are treated confidentially.</p>
        <button onClick={() => setSent(false)} className="mt-6 text-[#E8803A] text-sm font-medium hover:underline">Send another message</button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl">
      <h3 className="font-heading text-xl font-bold text-[#0C0C0E] mb-6">Send a Message</h3>
      {sendError && <p role="alert" className="mb-4 text-sm text-red-700" data-testid="contact-form-error">Sorry, your message didn't send. Please try again or email hello@brightridge.uk.</p>}
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
        <div className="flex gap-3 mb-2">
          {[{ val: "candidate", label: "I'm a Care Professional" }, { val: "provider", label: "I'm a Provider" }].map((opt) => (
            <button type="button" key={opt.val}
              onClick={() => setForm({ ...form, type: opt.val })}
              className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                form.type === opt.val
                  ? "bg-[#E8803A]/10 border-[#E8803A]/40 text-[#E8803A]"
                  : "bg-[#F5F5F3] border-[#E8E8E3] text-[#0C0C0E]/65 hover:border-[#E8803A]/20"
              }`}
              data-testid={`contact-type-${opt.val}`}
            >{opt.label}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input required placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full h-11 px-4 bg-[#F5F5F3] border border-[#E8E8E3] rounded-lg text-[#0C0C0E] text-sm focus:outline-none focus:ring-2 focus:ring-[#E8803A]/20 focus:border-[#E8803A]/30 placeholder:text-[#0C0C0E]/30"
            data-testid="contact-name" />
          <input required type="tel" placeholder="Phone Number *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full h-11 px-4 bg-[#F5F5F3] border border-[#E8E8E3] rounded-lg text-[#0C0C0E] text-sm focus:outline-none focus:ring-2 focus:ring-[#E8803A]/20 focus:border-[#E8803A]/30 placeholder:text-[#0C0C0E]/30"
            data-testid="contact-phone" />
        </div>
        <input required type="email" placeholder="Email Address *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full h-11 px-4 bg-[#F5F5F3] border border-[#E8E8E3] rounded-lg text-[#0C0C0E] text-sm focus:outline-none focus:ring-2 focus:ring-[#E8803A]/20 focus:border-[#E8803A]/30 placeholder:text-[#0C0C0E]/30"
          data-testid="contact-email" />
        <textarea placeholder="Tell us about what you're looking for..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3}
          className="w-full px-4 py-3 bg-[#F5F5F3] border border-[#E8E8E3] rounded-lg text-[#0C0C0E] text-sm focus:outline-none focus:ring-2 focus:ring-[#E8803A]/20 focus:border-[#E8803A]/30 placeholder:text-[#0C0C0E]/30 resize-none"
          data-testid="contact-message" />
        {form.type === "candidate" && (
          <div data-testid="contact-cv-upload">
            <label className="flex items-center gap-3 p-3 bg-[#F5F5F3] border border-dashed border-[#E8E8E3] rounded-lg cursor-pointer hover:border-[#E8803A]/30 transition-colors">
              <div className="w-9 h-9 rounded-lg bg-[#E8803A]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-[#E8803A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-[#0C0C0E]/60">{cvFile ? cvFile.name : "Attach your CV (optional)"}</span>
                <p className="text-[10px] text-[#0C0C0E]/30 mt-0.5">PDF, DOC, DOCX — Max 5MB</p>
              </div>
              <input type="file" className="hidden" accept=".pdf,.doc,.docx"
                onChange={(e) => setCvFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        )}
        <button type="submit" disabled={submitting} data-testid="contact-submit"
          className="w-full py-3.5 bg-[#E8803A] hover:bg-[#D66D2A] text-white font-semibold rounded-xl text-sm transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50">
          {submitting ? "Sending..." : "Send Message"}{!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
        <p className="text-center text-[#0C0C0E]/60 text-xs">We treat all enquiries confidentially. No spam, ever.</p>
      </form>
    </div>
  );
}
